<script setup>
import { computed, readonly, watch, ref } from 'vue';
import { useStore } from 'vuex';
import { useToast } from '@/composables/useToast';
import { getParsedMessage } from '@/data/error-messages';
import { OWNER_ROLES } from '@clearbanc/data-common-types';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';

import Modal from '@/components/shared/Dialog.vue';
import AlertMessage from '@/components/banners/AlertMessage.vue';
import UserSelectionButton from '@/pages/users/components/UserSelectionButton.vue';
import ModalControls from '@/components/modals/shared/ModalControls.vue';

// -----
// Setup
// -----

const store = useStore();
const toast = useToast();

const emit = defineEmits(['close']);

const props = defineProps({
  owner: {
    type: Object,
  },
  visible: {
    type: Boolean,
  },
});

// ---------
// Constants
// ---------
const requestStatuses = readonly({
  updateUser: computed(() => store.getters.requestStatus('UPDATE_USER')),
});

const errorMessage = computed(() => {
  if (requestStatuses.updateUser.isError) {
    return getParsedMessage(requestStatuses.updateUser.error);
  }
  return undefined;
});

const currentSignatory = ref(null);

// -------
// Actions
// -------

watch(
  () => props.visible,
  (newValue) => {
    if (newValue) {
      currentSignatory.value = store.getters.authorizedSignatory;
    }
  },
);

function closeModal() {
  emit('close');
}

async function confirmNewSignatoryChange() {
  const jobRoles = [...props.owner.jobRoles, OWNER_ROLES.SIGNATORY];
  await store.dispatchApiAction('UPDATE_USER', {
    id: props.owner.id,
    jobRoles,
    overrideSignatories: true,
  });
  if (requestStatuses.updateUser.isSuccess) {
    analytics.track(SEGMENT_EVENTS.if_user_set_as_signatory, {
      user_id: props.owner.id,
      job_roles: jobRoles,
    });
    closeModal();
    toast.success('Signatory updated.');
  }
}
</script>

<template>
  <Modal
    :visible="props.visible"
    header="Make User a Signatory?"
    :modal="true"
    :draggable="false"
    append-to="self"
    @update:visible="(visible) => !visible && closeModal()"
  >
    <div :class="$style['modal-content']">
      <p :class="$style.description">
        Contracts will be sent directly to the signatory's email on file.
      </p>
      <ul :class="$style.users">
        <li
          v-if="currentSignatory"
          :class="[$style.users__item, $style['users__item--disabled']]"
        >
          <UserSelectionButton
            disabled
            tag="Current"
            :owner="currentSignatory"
          />
        </li>

        <li v-if="props.owner" :class="$style.users__item">
          <UserSelectionButton tag="New" :owner="props.owner" />
        </li>
      </ul>
    </div>

    <template #footer>
      <AlertMessage v-if="errorMessage" type="error">
        {{ errorMessage }}
      </AlertMessage>

      <ModalControls
        :buttons="[
          {
            text: 'Cancel',
            action: closeModal,
            classes: ['p-button-outlined'],
            attributes: {},
          },
          {
            text: 'Confirm',
            action: confirmNewSignatoryChange,
            classes: [],
            loading: requestStatuses.updateUser.isPending,
            attributes: {
              loading: requestStatuses.updateUser.isPending,
            },
          },
        ]"
      />
    </template>
  </Modal>
</template>

<style lang="less" module>
.description {
  max-width: 500px;
  margin: 0;
}

.users {
  margin-top: 30px;
}

.users__item + .users__item {
  margin-top: 24px;
}

.users__item--disabled {
  opacity: 0.5;
}
</style>
